import React, { useEffect, useRef } from 'react';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';
import { MenuItem } from '@material-ui/core';
import Images from 'assets/img';
import TextField from 'components/TextField';
import { InputAdornment } from '@material-ui/core';
import {
  Container,
  Error,
  WrapperInput,
  WrapperInputCustom,
  InputRow,
  Column,
} from './styles';

// Componente wrapper para InputMask que preserva a posição do cursor (igual ao usado em ViagemForm)
const MaskedInput = ({ mask, value, onChange, onBlur, children, ...props }) => {
  const handleChange = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const oldValue = value || '';
    const newValue = input.value || '';

    onChange(e);

    setTimeout(() => {
      if (input && input.setSelectionRange) {
        const lengthDiff = newValue.length - oldValue.length;
        let newPosition = cursorPosition;

        if (lengthDiff > 0) {
          newPosition = Math.min(cursorPosition + lengthDiff, newValue.length);
        } else if (lengthDiff < 0) {
          newPosition = Math.max(0, cursorPosition + lengthDiff);
        }

        newPosition = Math.min(newPosition, newValue.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <InputMask
      {...props}
      mask={mask}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      alwaysShowMask={false}
      maskChar={null}
    >
      {children}
    </InputMask>
  );
};

function ProviderData({ formik, edit, view }) {
  const { providers } = useSelector((state) => state.providers);
  const lastProviderIdRef = useRef(null);

  const providersOptions = providers.map((item) => ({
    value: item.id,
    label: item.social_reason,
  }));

  // Atualizar campos quando provider_id mudar no Formik
  useEffect(() => {
    const providerId = formik.values.provider_id;
    
    // Evitar processamento se o providerId não mudou
    if (lastProviderIdRef.current === providerId) {
      return;
    }

    if (!providerId || !providers || providers.length === 0) {
      // Se não há providerId, limpar os campos
      if (providerId === '' && lastProviderIdRef.current !== null) {
        formik.setFieldValue('cnpj', '', false);
        formik.setFieldValue('owner', '', false);
        formik.setFieldValue('owner_phone', '', false);
        formik.setFieldValue('owner_email', '', false);
      }
      lastProviderIdRef.current = providerId;
      return;
    }

    // Encontrar o provider selecionado
    const provider = providers.find((prov) => prov.id === providerId);

    if (!provider) {
      lastProviderIdRef.current = providerId;
      return;
    }

    // Atualizar os campos quando o providerId mudar
    console.log('🔵 [ProviderData] Atualizando campos com provider:', provider);
    formik.setFieldValue('cnpj', provider.cnpj || '', false);
    formik.setFieldValue('owner', provider.owner || '', false);
    formik.setFieldValue('owner_phone', provider.phone || '', false);
    formik.setFieldValue('owner_email', provider.email || '', false);

    // Marcar que processamos este providerId
    lastProviderIdRef.current = providerId;
  }, [formik.values.provider_id, providers, formik]);

  const onChange = (e) => {
    const value = e.target.value;
    console.log('🔵 [ProviderData] onChange chamado, valor:', value);
    
    // Atualizar o campo provider_id no Formik
    formik.setFieldValue('provider_id', value, false);
    formik.setFieldError('owner', '');
    
    // Se houver um provider selecionado, atualizar os campos imediatamente
    if (value && providers && providers.length > 0) {
      const provider = providers.find((prov) => prov.id === value);
      if (provider) {
        console.log('🔵 [ProviderData] Provider encontrado, atualizando campos:', provider);
        formik.setFieldValue('cnpj', provider.cnpj || '', false);
        formik.setFieldValue('owner', provider.owner || '', false);
        formik.setFieldValue('owner_phone', provider.phone || '', false);
        formik.setFieldValue('owner_email', provider.email || '', false);
        
        // Atualizar o ref para evitar processamento duplicado no useEffect
        lastProviderIdRef.current = value;
      } else {
        console.warn('⚠️ [ProviderData] Provider não encontrado para o ID:', value);
      }
    } else if (value === '') {
      // Limpar campos se nenhum provider for selecionado
      formik.setFieldValue('cnpj', '', false);
      formik.setFieldValue('owner', '', false);
      formik.setFieldValue('owner_phone', '', false);
      formik.setFieldValue('owner_email', '', false);
      lastProviderIdRef.current = '';
    }
  };

  return (
    <Container>
      <InputRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Nome do Solicitante"
              name="provider_id"
              select
              value={formik.values.provider_id || ''}
              onChange={onChange}
              onBlur={formik.handleBlur}
              error={formik.touched.owner && Boolean(formik.errors.owner)}
              helperText={formik.touched.owner && formik.errors.owner}
              disabled={view}
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
            >
              <MenuItem value="">Selecione um solicitante</MenuItem>
              {providersOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </WrapperInputCustom>
        </Column>
        <Column>
          <WrapperInputCustom>
            <MaskedInput
              mask="99.999.999/9999-99"
              value={formik.values.cnpj || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  label="CNPJ"
                  fullWidth
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    ...inputProps.InputProps,
                    style: { color: '#000000' }, // Forçar cor preta para texto
                  }}
                />
              )}
            </MaskedInput>
          </WrapperInputCustom>
        </Column>
      </InputRow>

      <InputRow>
        <Column>
          <WrapperInputCustom>
            <TextField
              label="Responsável"
              name="owner"
              placeholder=""
              type="text"
              value={formik.values.owner || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                style: { color: '#000000' }, // Forçar cor preta para texto
              }}
            />
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <MaskedInput
              mask="(99) 99999-9999"
              value={formik.values.owner_phone || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Telefone"
                  fullWidth
                  color="secondary"
                  InputProps={{
                    ...inputProps.InputProps,
                    style: { color: '#000000' }, // Forçar cor preta para texto
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={Images.IconFlagBrasil} alt="Brasil" style={{ height: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </MaskedInput>
          </WrapperInputCustom>
        </Column>

        <Column>
          <WrapperInputCustom>
            <TextField
              label="E-mail"
              name="owner_email"
              placeholder=""
              value={formik.values.owner_email || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
              fullWidth
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                style: { color: '#000000' }, // Forçar cor preta para texto
              }}
            />
          </WrapperInputCustom>
        </Column>
      </InputRow>
    </Container>
  );
}

export default ProviderData;
